import { useCallback, useMemo, useState } from 'react';
import { useGetPromoSocialPostsQuery, useLazyGetPromoSocialPostsQuery } from '../../../../redux/api/fymAPI';
import { GetPromoSocialPostsResponse, SerializedSocialPost, SocialPlatform, SocialPostType } from '../types';

export interface UseSocialPostsProps {
  accountId: number;
  /** The period in days. The server limits it to the history window of the plan. */
  days: number;
  platform?: SocialPlatform;
  type?: SocialPostType;
  /** True: only the posts that Music24 published. */
  ours?: boolean;
  /** True when no channel is connected: the request does not go out. */
  skip?: boolean;
}

interface LoadedPages {
  /** The filters these pages belong to. Pages of other filters are ignored. */
  key: string;
  /**
   * The first page these pages continue. A refetch that changes the first page gives a new
   * object, and the cursors of the old one are then stale. A refetch with equal data keeps the object.
   */
  source: GetPromoSocialPostsResponse;
  posts: SerializedSocialPost[];
  nextCursor: string | null;
}

/**
 * The posts of the artist on every channel: `GET promo/social-posts?days&platform&type&ours&cursor`.
 * Newest first, 50 per page. `loadMore` reads the page after the last cursor and adds it to the
 * list. A change of a filter starts the list again, and so does a change of the first page.
 */
export function useSocialPosts({ accountId, days, platform, type, ours, skip = false }: UseSocialPostsProps) {
  const first = useGetPromoSocialPostsQuery({ accountId, days, platform, type, ours }, { skip });
  const [loadPage, { isFetching: isLoadingMore, error: loadMoreError }] = useLazyGetPromoSocialPostsQuery();
  const [loaded, setLoaded] = useState<LoadedPages>();

  const key = JSON.stringify([accountId, days, platform ?? null, type ?? null, ours ?? null]);
  const firstPage = first.currentData;
  const more = loaded !== undefined && loaded.key === key && loaded.source === firstPage ? loaded : undefined;

  const posts = useMemo<SerializedSocialPost[] | undefined>(
    () => (firstPage === undefined ? undefined : [...firstPage.posts, ...(more?.posts ?? [])]),
    [firstPage, more]
  );
  const nextCursor = more === undefined ? (firstPage?.nextCursor ?? null) : more.nextCursor;

  const loadMore = useCallback(async (): Promise<void> => {
    if (nextCursor === null || firstPage === undefined) {
      return;
    }
    const result = await loadPage({ accountId, days, platform, type, ours, cursor: nextCursor });
    const page = result.data;
    if (page === undefined) {
      return;
    }
    setLoaded((previous) => ({
      key,
      source: firstPage,
      posts: [...(previous?.key === key && previous.source === firstPage ? previous.posts : []), ...page.posts],
      nextCursor: page.nextCursor,
    }));
  }, [accountId, days, firstPage, key, loadPage, nextCursor, ours, platform, type]);

  const { refetch } = first;
  const retry = useCallback(() => {
    setLoaded(undefined);
    refetch().catch(console.error);
  }, [refetch]);

  return {
    posts,
    hasMore: nextCursor !== null,
    isLoading: first.isFetching && firstPage === undefined,
    isLoadingMore,
    error: first.error,
    loadMoreError,
    retry,
    loadMore,
  };
}
